import React, { useMemo } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
} from 'tea-component';
import { getLocale } from 'umi';
import { useReportName, useSubmitReport } from './hooks';
import './index.scss';
import { LocaleType } from '@/pages/opinion/types/common';
import useRenderComponent from '@op/components/FilterCommon/mainComponents/FilterBox/useRenderComponent';
import RenderGameIcon from '@op/components/RenderImg/renderGameIcon';
import GameSelector from '@op/components/GameSelector';
import useLanguage from '@/pages/opinion/hooks/useLanguage';
import { openWindowUrl } from '@/pages/opinion/utils/url';
import LoadingCommon from '../../../LoadingCommon';
import { NO_DATE } from '@/pages/opinion/constants';

const options = [
  { value: 'dashboard', text: 'Dashboard' },
  { value: 'intelligence', text: 'Intelligence' },
  { value: 'opinions', text: 'Opinions' },
];

const SaveReport = ({
  visible,
  close,
  pagePath,
  gameInfo,
  userInfo,
  wholeGameInfo,
  addCallback,
  defaultFilterValue = {},
  defaultSelectedFilter,
  editInfo,
  originalName,
}: any) => {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const locale: LocaleType = useMemo(() => getLocale(), [formatMessage]);

  const { report_name, id } = useMemo(() => {
    if (editInfo) {
      return editInfo;
    }

    return { report_name: originalName };
  }, [editInfo]);

  const { checkFavoritesValue, reportName, setReportName, disable, errorText } =
    useReportName({
      gameInfo,
      id,
      report_name,
    });

  // !如果这里提交没有相关组件的数据，要注意是否配置了组件key映射数据key： filterKeyMapValueKey
  const {
    pageImage,
    submit,
    filterOptions,
    optionsHeight,
    filterParams,
    selectedFilterOptions,
    setSelectedFilterOptions,
    originalUrl,
    systemValue,
    setSystemValue,
  } = useSubmitReport({
    pagePath,
    gameInfo,
    addCallback,
    defaultSelectedFilter,
    id: id,
    editInfo,
  });

  const { renderComponentElement, dealwithBackData, loading } =
    useRenderComponent({
      filter: filterOptions,
      confirm: () => {},
      gameInfo,
      userInfo,
      lang: locale,
      defaultValue: defaultFilterValue,
      isTile: true,
      wholeGameInfo,
      ...filterParams,
      filterItemRender: (key: string, element: JSX.Element) => {
        return <Checkbox name={key}>{element}</Checkbox>;
      },
    });
  return (
    <Modal
      size={800}
      className="dbh-save-report"
      visible={visible}
      caption={formatMessage({
        id: `${editInfo ? 'Edit' : 'Save'} Report`,
      })}
      onClose={close}
    >
      <Modal.Body>
        <Form>
          <Form.Item
            label={formatMessage({ id: 'Save to System' })}
            showStatusIcon={false}
          >
            <Select
              disabled
              appearance="button"
              className="dbh-save-report-input"
              options={formatOptionsMessage(options)}
              value={systemValue}
              matchButtonWidth
              onChange={(value) => setSystemValue(value)}
              // placeholder={formatMessage({ id: 'Change System' })}
            />
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'Game' })}
            showStatusIcon={false}
          >
            <Dropdown
              disabled
              button={
                <div className="dbh-save-report-game">
                  <RenderGameIcon
                    gameInfo={wholeGameInfo}
                    className="dbh-save-report-game-img"
                  ></RenderGameIcon>
                  <span className="dbh-save-report-game-text">
                    {wholeGameInfo.entity_name}
                  </span>
                </div>
              }
              className="dbh-save-report-input"
              matchButtonWidth
              appearance="button"
              clickClose={false}
            >
              {(close) => (
                <div>
                  <GameSelector />
                </div>
              )}
            </Dropdown>
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'Report Name' })}
            showStatusIcon={false}
            status={!!errorText ? 'error' : undefined}
            message={formatMessage({ id: errorText || NO_DATE.EMPTY })}
          >
            <Input
              value={reportName}
              onChange={(value: string) => {
                setReportName(value);
                checkFavoritesValue(value);
              }}
              className="dbh-save-report-input"
              placeholder={formatMessage({
                id: 'Please input a name for this report',
              })}
            />
          </Form.Item>
          {filterOptions.length > 0 && (
            <Form.Item
              label={formatMessage({ id: 'Filter Settings' })}
              showStatusIcon={false}
            >
              <Form className="dbh-save-report-box">
                <Checkbox.Group
                  value={selectedFilterOptions}
                  onChange={(value: any) => {
                    setSelectedFilterOptions(value);
                  }}
                >
                  {loading ? (
                    <LoadingCommon
                      status="loading"
                      style={{ height: optionsHeight }}
                    />
                  ) : (
                    renderComponentElement
                  )}
                </Checkbox.Group>
              </Form>
            </Form.Item>
          )}

          <Form.Item
            label={formatMessage({ id: 'Original URL' })}
            showStatusIcon={false}
          >
            <Button
              type="link"
              onClick={() => openWindowUrl(originalUrl)}
              className="dbh-save-report-link"
            >
              {originalUrl}
            </Button>
          </Form.Item>
          {pageImage && (
            <Form.Item
              label={formatMessage({ id: 'Preview' })}
              showStatusIcon={false}
            >
              <img src={pageImage} className="dbh-save-report-img" />
            </Form.Item>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={disable}
          onClick={() => {
            submit(dealwithBackData, reportName, close);
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveReport;
